<template>
  <!-- Phase 5: + bulk selection / group actions (shell selected). -->
  <div class="plm" :class="{ 'plm--selecting': selectionMode }" dir="rtl">
    <div class="plm__header">
      <div class="plm__bar">
        <div class="plm__bar-actions">
          <button
            type="button"
            class="plm__bar-btn plm__bar-btn--create"
            :aria-label="
              $route.name == 'ProductList' ? $t('newProduct') : $t('newService')
            "
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="plm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="plm__title">
          {{
            productList.titleProp
              ? $t(productList.titleProp)
              : $route.name == "ProductList"
              ? $t("productList")
              : $route.name == "VariantsList" || $route.name == "Variants"
              ? $t("variant-product")
              : $t("serviceList")
          }}
        </h1>
        <button
          type="button"
          class="plm__bar-btn plm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="plm__search-row">
        <div class="plm__search">
          <v-icon size="20" color="#90A4AE" class="plm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="productList.search"
            type="search"
            class="plm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="productList.search"
            type="button"
            class="plm__search-clear"
            :aria-label="$t('clear')"
            @click="productList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="plm__filter-btn"
          :class="{ 'plm__filter-btn--active': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
        </button>
        <button
          type="button"
          class="plm__filter-btn"
          :class="{ 'plm__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="plm__select-bar" dir="rtl">
        <button
          type="button"
          class="plm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="plm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="plm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="productList.loading && !displayItems.length" class="plm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="plm__state plm__state--empty">
      <img
        class="plm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="plm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="plm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="plm-card"
        :class="{ 'plm-card--selected': selectionMode && isSelected(item) }"
      >
        <div
          v-if="selectionMode"
          class="plm-card__select-overlay"
          :class="{ 'plm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="plm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="plm-card__head">
          <div class="plm-card__identity">
            <img
              class="plm-card__avatar"
              :src="avatarSrc(item)"
              alt=""
            />
            <div class="plm-card__title-block">
              <button
                type="button"
                class="plm-card__name-link"
                @click="productList.edit(item)"
              >
                <span class="plm-card__name">{{ cardName(item) }}</span>
                <v-icon size="12" color="#879EB6">mdi-pencil-outline</v-icon>
                <v-icon
                  v-if="item.bundle"
                  size="14"
                  color="#00897B"
                  class="ms-1"
                >
                  mdi-package-variant-closed
                </v-icon>
              </button>
              <span v-if="item.code" class="plm-card__code">{{ item.code }}</span>
            </div>
          </div>
          <span class="plm-card__number">#{{ item.id }}</span>
        </div>

        <div class="plm-card__amounts">
          <div class="plm-card__amount-row">
            <span class="plm-card__amount-label">{{ $t("unit") }}</span>
            <span class="plm-card__amount-value">
              {{ item.unit ? item.unit.fame : "—" }}
            </span>
          </div>
          <div class="plm-card__amount-row">
            <span class="plm-card__amount-label">{{ $t("price") }}</span>
            <span class="plm-card__amount-value">
              {{ $numberFormat(parseFloat(item.price || 0)) }}
              {{ $store.state.currencySymbol }}
            </span>
          </div>
          <div class="plm-card__amount-row">
            <span class="plm-card__amount-label">{{ $t("category") }}</span>
            <span class="plm-card__amount-value plm-card__amount-value--muted">
              {{ categoryText(item) }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="plm-card__footer">
          <div class="plm-card__actions">
            <button
              v-show="removePermission(productList.typeForm)"
              type="button"
              class="plm-btn plm-btn--danger"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              type="button"
              class="plm-btn plm-btn--primary"
              @click="productList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="plm-btn plm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            type="button"
            class="plm-stock-btn"
            :disabled="!!item.bundle"
            @click="goStockRoute(item)"
          >
            <v-icon
              size="14"
              :color="item.bundle ? '#90A4AE' : '#5B7C99'"
            >
              mdi-warehouse
            </v-icon>
            {{ $t("stock") }} {{ stockText(item) }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="plm__state plm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="plm__sentinel" />

    <div v-if="selectionMode" class="plm-bulkbar" dir="rtl">
      <button
        type="button"
        class="plm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkBarcode"
      >
        <v-icon size="19" color="#5B7C99">mdi-barcode</v-icon>
        {{ $t("productBarcode") }}
      </button>
      <button
        v-if="hasPerm('rebuildGoodCost')"
        type="button"
        class="plm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkRebuildGoodCost"
      >
        <v-icon size="19" color="#5B7C99">mdi-wrench-outline</v-icon>
        {{ $t("rebuildGoodCost") }}
      </button>
      <button
        type="button"
        class="plm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkUpdateInventory"
      >
        <v-icon size="19" color="#5B7C99">mdi-autorenew</v-icon>
        {{ $t("Updating Product Inventory in the Integrator") }}
      </button>
      <button
        v-if="removePermission(productList.typeForm)"
        type="button"
        class="plm-bulkbar__btn plm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <!-- Details sheet: product fields + quick actions (same shell methods) -->
    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="plm-sheet">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <div class="plm-sheet__header-side">
            <span v-if="detailItem" class="plm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="plm-sheet__title">{{ $t("details") }}</div>
          <div class="plm-sheet__header-side plm-sheet__header-side--end">
            <button
              type="button"
              class="plm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="plm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="plm-sheet__quick"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            type="button"
            class="plm-sheet__quick"
            @click="runNamedAction('turnover')"
          >
            <v-icon size="18" color="#5B7C99">mdi-recycle-variant</v-icon>
            {{ $t("productTurnover") }}
          </button>
          <button
            v-if="hasPerm('productCard')"
            type="button"
            class="plm-sheet__quick"
            @click="runNamedAction('openProductCard')"
          >
            <v-icon size="18" color="#5B7C99">
              mdi-card-bulleted-outline
            </v-icon>
            {{ $t("productCard") }}
          </button>
          <button
            type="button"
            class="plm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="plm-sheet__name-block" dir="rtl">
          <div class="plm-sheet__name-row">
            <span class="plm-sheet__row-label">{{ $t("product-name") }}</span>
            <span class="plm-sheet__name-value">
              {{ detailItem.name || "—" }}
            </span>
          </div>
          <button
            v-if="!detailItem.bundle"
            type="button"
            class="plm-sheet__linkrow"
            @click="runNamedAction('entrepotTurnover')"
          >
            <v-icon size="18" color="#00796B">mdi-warehouse</v-icon>
            <span class="plm-sheet__linkrow-text">
              {{ $t("stock") }}: {{ stockText(detailItem) }}
            </span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="plm-sheet__body" dir="rtl">
          <div class="plm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="plm-sheet__row"
            >
              <span class="plm-sheet__row-label">{{ row.label }}</span>
              <span class="plm-sheet__row-value">{{ row.value }}</span>
            </div>
            <div
              v-if="
                detailItem.custom_fields && detailItem.custom_fields.length
              "
              class="plm-sheet__row plm-sheet__row--chips"
            >
              <v-chip
                v-for="(field, fi) in detailItem.custom_fields"
                :key="'detail-cf-' + fi"
                label
                color="primary"
                small
                class="px-1"
              >
                <span class="px-1 white--text">
                  {{ field.key }} : {{ field.value }}
                </span>
              </v-chip>
            </div>
            <div
              v-for="(idRow, ii) in detailIdRowsList"
              :key="'detail-id-' + ii"
              class="plm-sheet__row plm-sheet__row--id"
            >
              <span class="plm-sheet__row-label plm-sheet__id-label">
                <img
                  v-if="idRow.icon"
                  class="plm-sheet__id-icon"
                  :src="idRow.icon"
                  alt=""
                />
                {{ idRow.label }}
              </span>
              <button
                type="button"
                class="plm-sheet__row-value plm-sheet__id-value"
                v-clipboard:copy="String(idRow.value || '')"
                @click="onCopyValue"
              >
                {{ idRow.value }}
              </button>
            </div>
            <div
              v-if="detailItem.content"
              class="plm-sheet__row"
            >
              <span class="plm-sheet__row-label">{{ $t("description") }}</span>
              <span class="plm-sheet__row-value plm-sheet__row-value--muted">
                {{ detailItem.content }}
              </span>
            </div>
            <div class="plm-sheet__row">
              <span class="plm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="plm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span
                v-else
                class="plm-sheet__row-value plm-sheet__row-value--muted"
              >
                —
              </span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Secondary actions from the desktop row menu -->
    <v-bottom-sheet v-model="moreSheet">
      <v-card class="plm-sheet">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <span class="plm-sheet__header-spacer" />
          <div class="plm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="plm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="plm-sheet__body" dir="rtl">
          <div class="plm-sheet__actions plm-sheet__actions--sheet">
            <button
              type="button"
              class="plm-sheet__action"
              @click="runNamedAction('turnoverCount')"
            >
              <v-icon size="19" color="#5B7C99">
                mdi-ballot-recount-outline
              </v-icon>
              {{ $t("productCountTurnover") }}
            </button>
            <button
              type="button"
              class="plm-sheet__action"
              @click="runNamedAction('turnoverAmount')"
            >
              <v-icon size="19" color="#5B7C99">mdi-chart-gantt</v-icon>
              {{ $t("productAmountTurnover") }}
            </button>
            <button
              v-if="detailItem.variants_count"
              type="button"
              class="plm-sheet__action"
              @click="runNamedAction('goToVariants')"
            >
              <v-icon size="19" color="#5B7C99">mdi-file-tree-outline</v-icon>
              {{ $t("variant-product") }}
              ({{ detailItem.variants_count }})
            </button>
            <button
              type="button"
              class="plm-sheet__action"
              @click="runNamedAction('barcode')"
            >
              <v-icon size="19" color="#5B7C99">mdi-barcode</v-icon>
              {{ $t("barcode") }}
            </button>
            <button
              v-if="hasPerm('rebuildGoodCost')"
              type="button"
              class="plm-sheet__action"
              @click="runNamedAction('rebuildGoodCost')"
            >
              <v-icon size="19" color="#5B7C99">mdi-wrench-outline</v-icon>
              {{ $t("rebuildGoodCost") }}
            </button>
            <button
              type="button"
              class="plm-sheet__action"
              v-clipboard:copy="productEditLink(detailItem)"
              @click="onCopyLink"
            >
              <v-icon size="19" color="#5B7C99">mdi-link</v-icon>
              {{ $t("copy-link") }}
            </button>
            <div class="plm-sheet__action plm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="productList.typeForm"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Mobile delete confirmation: same shell remove()/removegroup() -->
    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="plm-confirm-sheet" dir="rtl">
        <div class="plm-confirm-sheet__handle" />
        <div class="plm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="plm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="plm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="plm-confirm-sheet__rows">
          <div class="plm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="plm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="plm-confirm-sheet__item"
            >
              <span class="plm-confirm-sheet__item-main">
                <span class="plm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="plm-confirm-sheet__item-name">
                  {{ sel.name || "—" }}
                </span>
              </span>
              <span v-if="sel.code" class="plm-confirm-sheet__item-number">
                #{{ sel.code }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else-if="productList.deleteItem"
          class="plm-confirm-sheet__rows"
        >
          <div class="plm-confirm-sheet__row">
            <span>{{ $t("product-name") }}</span>
            <strong>{{ productList.deleteItem.name || "—" }}</strong>
          </div>
          <div
            v-if="productList.deleteItem.code"
            class="plm-confirm-sheet__row"
          >
            <span>{{ $t("code") }}</span>
            <strong>{{ productList.deleteItem.code }}</strong>
          </div>
          <div class="plm-confirm-sheet__row">
            <span>{{ $t("id") }}</span>
            <strong>#{{ productList.deleteItem.id }}</strong>
          </div>
        </div>
        <div class="plm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="plm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="plm-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop filter dialog -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="plm-sheet plm-sheet--filter">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <span class="plm-sheet__header-spacer" />
          <div class="plm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="plm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="plm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="plm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="plm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="plm-filter-nav__texts">
              <span class="plm-filter-nav__item-title">{{ group.title }}</span>
              <span class="plm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="plm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="plm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="plm-sheet__btn plm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="plm-sheet__btn plm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="plm-sheet plm-sheet--filter">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <span class="plm-sheet__header-spacer" />
          <div class="plm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="plm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="plm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'status'">
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("status") }}</div>
              <!-- Multi+clearable like desktop v-select: independent checks -->
              <div class="plm-check-list plm-check-list--row">
                <button
                  v-for="item in productList.productStatusItems || []"
                  :key="'ps-' + item.value"
                  type="button"
                  class="plm-check-row"
                  :class="{
                    'plm-check-row--on': isProductStatusOn(item.value),
                  }"
                  @click="pickProductStatus(item.value)"
                >
                  <v-icon
                    size="22"
                    :color="
                      isProductStatusOn(item.value) ? '#5B7C99' : '#90A4AE'
                    "
                  >
                    {{
                      isProductStatusOn(item.value)
                        ? "mdi-checkbox-marked-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  <span class="plm-check-row__label">{{ item.text }}</span>
                </button>
              </div>
            </div>
            <div class="plm-toggle-row">
              <button
                type="button"
                class="plm-toggle"
                :class="{
                  'plm-toggle--on': productList.bundling === 'active',
                }"
                @click="toggleBundling"
              >
                <span class="plm-toggle__label">{{ $t("showJustBundle") }}</span>
                <span class="plm-toggle__switch" aria-hidden="true" />
              </button>
            </div>
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">
                {{ $t("inventoryStatus") }}
              </div>
              <!-- Same single clearable select as desktop inventoryStatus -->
              <button
                type="button"
                class="plm-f-picker-field"
                @click="inventoryPickerOpen = true"
              >
                <span class="plm-f-picker-field__text">
                  {{ inventoryStatusLabel }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("main-group") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plGroup')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plGroup", productList.group) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="plGroup"
                  v-model="productList.group"
                  :title="$t('main-group')"
                  type="productGroup"
                  :data="JSON.stringify({ parent: null, type: 'group' })"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("other-group") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plCat1')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plCat1", productList.cat1) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="plCat1"
                  v-model="productList.cat1"
                  :title="$t('other-group')"
                  type="productGroup"
                  :data="JSON.stringify({ parent: null, type: 'category' })"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("category") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plCat2')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plCat2", productList.cat2) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="plCat2"
                  v-model="productList.cat2"
                  title="category"
                  type="productGroup"
                  :data="JSON.stringify({ parent: null, type: 'subcategory' })"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plTag')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plTag", productList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <tag ref="plTag" v-model="productList.tag" />
              </div>
            </div>
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plBranch')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plBranch", productList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="plBranch"
                  v-model="productList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'registerar'">
            <div class="plm-f-field-wrap">
              <div class="plm-f-field-wrap__label">{{ $t("registerar") }}</div>
              <button
                type="button"
                class="plm-f-picker-field"
                @click="openPicker('plRegisterar')"
              >
                <span class="plm-f-picker-field__text">
                  {{ pickerSummary("plRegisterar", productList.registerar) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="plm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="plRegisterar"
                  v-model="productList.registerar"
                  :title="$t('registerar')"
                  type="partners"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'custom'">
            <div class="plm-cf__hint">
              {{ $t("clickForAddCustomeField") }}
            </div>
            <button
              type="button"
              class="plm-cf__add"
              @click="addFilterCustomField"
            >
              <v-icon size="18" color="#5B7C99">mdi-plus</v-icon>
              <span>{{ $t("addCustomeField") }}</span>
            </button>
            <div v-if="!filterCustomItems.length" class="plm-cf__empty">
              {{ $t("invoice-mobile-custom-fields-empty") }}
            </div>
            <div
              v-for="(item, index) in filterCustomItems"
              :key="'filter-custom-' + index"
              class="plm-cf-card"
            >
              <div class="plm-cf-card__type">
                <div class="plm-f-field-wrap plm-cf-card__type-field">
                  <div class="plm-f-field-wrap__label">
                    {{ $t("additionalFields") }}
                  </div>
                  <v-combobox
                    v-model="item.key"
                    :items="filterCustomKeyItems"
                    :search-input.sync="filterCustomSearch"
                    solo
                    flat
                    dense
                    hide-details
                    hide-selected
                    height="40"
                    append-icon="mdi-chevron-down"
                    class="plm-f-field"
                    @change="syncFilterCustomFields"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ $t("toCreate") }}: "<strong>{{
                              filterCustomSearch
                            }}</strong
                            >". {{ $t("doEnter") }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-combobox>
                </div>
                <button
                  type="button"
                  class="plm-cf-card__remove"
                  :aria-label="$t('remove')"
                  @click="removeFilterCustomField(index)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>
              <div class="plm-f-field-wrap">
                <div class="plm-f-field-wrap__label">{{ $t("value") }}</div>
                <input
                  v-model="item.value"
                  type="text"
                  class="plm-cf-card__input plm-cf-card__input--text"
                  @input="syncFilterCustomFields"
                />
              </div>
            </div>
          </template>
        </div>

        <div class="plm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="plm-sheet__btn plm-sheet__btn--primary"
            @click="backToFilterMain"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Inventory status dropdown layer (desktop single clearable v-select) -->
    <v-bottom-sheet v-model="inventoryPickerOpen">
      <v-card class="plm-sheet plm-sheet--filter">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <span class="plm-sheet__header-spacer" />
          <div class="plm-sheet__title">{{ $t("inventoryStatus") }}</div>
          <button
            type="button"
            class="plm-sheet__back"
            :aria-label="$t('back')"
            @click="inventoryPickerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="plm-option-list" dir="rtl">
          <button
            type="button"
            class="plm-option-list__item"
            :class="{
              'plm-option-list__item--on': !productList.inventoryStatus,
            }"
            @click="pickInventoryStatus(null)"
          >
            <span>{{ $t("clear") }}</span>
            <v-icon
              v-if="!productList.inventoryStatus"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-for="item in productList.inventoryStatusItems || []"
            :key="'inv-opt-' + item.value"
            type="button"
            class="plm-option-list__item"
            :class="{
              'plm-option-list__item--on':
                productList.inventoryStatus === item.value,
            }"
            @click="pickInventoryStatus(item.value)"
          >
            <span>{{ item.text }}</span>
            <v-icon
              v-if="productList.inventoryStatus === item.value"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "ProductListMobile",
  inject: ["productList"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      filterCustomItems: [],
      filterCustomKeyItems: [],
      filterCustomSearch: null,
      filterCustomKeyTimer: null,
      inventoryPickerOpen: false,
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
    };
  },
  computed: {
    currentPage() {
      const opts = this.productList.tableOptions || {};
      return opts.page || 1;
    },
    inventoryStatusLabel() {
      const val = this.productList.inventoryStatus;
      if (!this.hasValue(val)) return "—";
      const found = (this.productList.inventoryStatusItems || []).find(
        (i) => i && i.value === val
      );
      return found && found.text ? found.text : String(val);
    },
    filterGroups() {
      const groups = [
        {
          key: "status",
          icon: "mdi-check-circle-outline",
          title: this.$t("status"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("category"),
        },
        {
          key: "registerar",
          icon: "mdi-account-outline",
          title: this.$t("registerar"),
        },
      ];
      if (this.customFieldsPermission) {
        groups.push({
          key: "custom",
          icon: "mdi-playlist-plus",
          title: this.$t("customFields"),
        });
      }
      return groups;
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    hasAnyFilter() {
      return (
        this.groupHasValue("status") ||
        this.groupHasValue("category") ||
        this.groupHasValue("registerar") ||
        this.groupHasValue("custom")
      );
    },
    detailIdRowsList() {
      return this.detailIdRows(this.detailItem);
    },
  },
  watch: {
    filterCustomSearch() {
      this.loadFilterCustomKeys();
    },
    "productList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "productList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.productList.tableOptions = Object.assign(
          {},
          this.productList.tableOptions,
          { page: 1 }
        );
      }
    },
    "productList.selected"(val) {
      // Shell clears selected after removegroup / rebuildGoodCost success.
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
  },
  mounted() {
    this.displayItems = (this.productList.items || []).slice();
    // Assigning tableOptions triggers shell watcher -> getItems (same as desktop).
    this.productList.tableOptions = Object.assign(
      {},
      this.productList.tableOptions,
      { page: 1, itemsPerPage: 10 }
    );
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    clearTimeout(this.filterCustomKeyTimer);
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      if (typeof this.productList.goTo === "function") {
        this.productList.goTo(
          this.$route.name == "ProductList" ? "Product" : "Service"
        );
        return;
      }
      const name =
        this.$route.name == "ProductList" ? "Product" : "Service";
      this.$router.push({ name }).catch(() => {});
    },
    onRefreshTap() {
      this.appending = false;
      this.productList.tableOptions = Object.assign(
        {},
        this.productList.tableOptions,
        { page: 1 }
      );
      this.productList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    avatarSrc(item) {
      if (item && item.avatar && item.avatar.link) return item.avatar.link;
      return "/img/noImage.png";
    },
    cardName(item) {
      const name = item && item.name ? String(item.name).trim() : "";
      if (!name) return "—";
      const max = 40;
      if (name.length <= max) return name;
      return name.slice(0, max).trim() + "…";
    },
    goStockRoute(item) {
      if (!item || item.bundle) return;
      if (typeof this.productList.entrepotTurnover === "function") {
        this.productList.entrepotTurnover(item, null);
      }
    },
    stockText(item) {
      if (!item) return "—";
      if (item.bundle) {
        return item.stockBundle && item.stockBundle.count != null
          ? item.stockBundle.count
          : 0;
      }
      return item.stock && item.stock.count != null ? item.stock.count : 0;
    },
    categoryText(item) {
      if (!item) return "—";
      const parts = [];
      if (item.group && item.group.name) parts.push(item.group.name);
      if (item.cat1 && item.cat1.name) parts.push(item.cat1.name);
      if (item.cat2 && item.cat2.name) parts.push(item.cat2.name);
      if (!parts.length) return this.$t("withoutCategory");
      return parts.join("، ");
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.productList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.productList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.productList.selected && this.productList.selected.length > 0) {
        this.productList.removegroup();
      } else {
        this.productList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    selectedRowNumber(item) {
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    bulkBarcode() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.productList.barcode();
    },
    bulkRebuildGoodCost() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.productList.rebuildGoodCost();
    },
    bulkUpdateInventory() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.productList.upateProdyctInventory();
    },
    hasPerm(name) {
      const perms = this.$store.state.permissions;
      return !!(perms && perms.includes(name));
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.productList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.productList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.productList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    productEditLink(item) {
      if (!item) return "";
      const path =
        this.productList.typeForm == "product" ? "product" : "service";
      return this.$baseURL + "/" + path + "/edit/" + item.id;
    },
    onCopyLink() {
      this.moreSheet = false;
      if (typeof this.copied === "function") this.copied();
      else if (
        this.productList &&
        typeof this.productList.copied === "function"
      ) {
        this.productList.copied();
      }
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      const list = this.productList;
      this.moreSheet = false;
      if (name === "remove") {
        this.askDelete(item);
        return;
      }
      this.detailSheet = false;
      if (name === "edit") list.edit(item);
      else if (name === "turnover") list.turnover(item, null);
      else if (name === "turnoverCount") list.turnoverCount(item, null);
      else if (name === "turnoverAmount") list.turnoverAmount(item, null);
      else if (name === "openProductCard") list.openProductCard(item);
      else if (name === "entrepotTurnover") list.entrepotTurnover(item, null);
      else if (name === "barcode" && typeof list.barcodeSingle === "function") {
        list.barcodeSingle(item);
      } else if (
        name === "rebuildGoodCost" &&
        typeof list.rebuildGoodCostItem === "function"
      ) {
        list.rebuildGoodCostItem(item);
      } else if (
        name === "goToVariants" &&
        typeof list.goToVariants === "function"
      ) {
        list.goToVariants(item);
      }
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
        });
      push(this.$t("id"), item.id);
      push(this.$t("code") + this.$t("product"), item.code);
      push(this.$t("unit"), item.unit ? item.unit.fame : null);
      push(
        this.$t("price"),
        this.$numberFormat(parseFloat(item.price || 0)) +
          " " +
          (this.$store.state.currencySymbol || "")
      );
      push(this.$t("stock"), this.stockText(item));
      push(this.$t("category"), this.categoryText(item));
      push(
        this.$t("productKind"),
        item.kind && item.kind.name ? item.kind.name : this.$t("product")
      );
      push(this.$t("barcode"), item.barcode);
      push(this.$t("orderPoint"), item.order_point);
      push(
        this.$t("displayPriority"),
        item.meta && item.meta.priority != null ? item.meta.priority : null
      );
      push(
        this.$t("variant-product"),
        item.variants_count != null && item.variants_count !== ""
          ? item.variants_count
          : null
      );
      push(this.$t("registerar"), item.user && item.user.name);
      return rows;
    },
    detailIdRows(item) {
      // Same integrator IDs as desktop "سایر", one row each (like CustomerListMobile).
      if (!item) return [];
      const lines = [];
      const add = (icon, label, value) => {
        if (value == null || value === "") return;
        lines.push({ icon: icon || null, label, value: String(value) });
      };
      const panelName = (obj) =>
        obj && obj.panels && obj.panels.name ? obj.panels.name : "-";
      const icons = {
        woo: require("@/assets/woo.png"),
        markito: require("@/assets/markitoLogo.png"),
        portal: require("@/assets/portal_no_bg.png"),
        portalOld: require("@/assets/portal.png"),
        sazito: require("@/assets/sazito.png"),
        mixin: require("@/assets/integrators/mixin.png"),
        snap: require("@/assets/integrators/snapShop.png"),
        webzi: require("@/assets/webzi.png"),
        digikala: require("@/assets/digikala.png"),
        basalam: require("@/assets/basalam.png"),
      };
      (item.woocommerceItems || []).forEach((woo) => {
        if (!woo) return;
        add(icons.woo, this.$t("id"), woo.woocommerceId);
        add(icons.woo, this.$t("variable"), woo.woocommerceVariantId);
      });
      if (item.integrator) {
        add(icons.markito, this.$t("id"), item.integrator.markitoId);
        add(icons.webzi, this.$t("id"), item.integrator.webziId);
        add(icons.webzi, this.$t("variable"), item.integrator.webziVariantId);
        add(icons.portalOld, this.$t("id"), item.integrator.portalId);
        add(
          icons.portalOld,
          this.$t("variable"),
          item.integrator.portalVariantId
        );
      }
      (item.portal_product || []).forEach((obj) => {
        add(icons.portal, this.$t("id"), obj.portal_id);
        add(icons.portal, this.$t("portalVariantId"), obj.portal_variant_id);
      });
      (item.sazito_product || []).forEach((obj) => {
        add(
          icons.sazito,
          this.$t("sazitoId") + " (" + panelName(obj) + ")",
          obj.sazito_id
        );
        add(
          icons.sazito,
          this.$t("sazitoVariantId") + " (" + panelName(obj) + ")",
          obj.sazito_dynamic_id
        );
      });
      (item.mixin_product || []).forEach((obj) => {
        add(
          icons.mixin,
          this.$t("mixinId") + " (" + panelName(obj) + ")",
          obj.mixin_id
        );
        add(
          icons.mixin,
          this.$t("mixinVariantId") + " (" + panelName(obj) + ")",
          obj.mixin_variant_id
        );
      });
      (item.snapshop_product || []).forEach((obj) => {
        add(
          icons.snap,
          this.$t("snapshopId") + " (" + panelName(obj) + ")",
          obj.snapshop_id
        );
        add(
          icons.snap,
          this.$t("snapshopVariantId") + " (" + panelName(obj) + ")",
          obj.snapshop_variant_id
        );
      });
      (item.digikala_product || []).forEach((obj) => {
        add(
          icons.digikala,
          "DKP (" + panelName(obj) + ")",
          obj.digikala_product_id
        );
        add(
          icons.digikala,
          "DKPC (" + panelName(obj) + ")",
          obj.product_variant_id
        );
      });
      (item.basalamIntegrator || []).forEach((obj) => {
        const panel =
          obj.basalamPanelName && obj.basalamPanelName.name
            ? obj.basalamPanelName.name
            : "-";
        add(icons.basalam, this.$t("id") + " (" + panel + ")", obj.basalamId);
        add(
          icons.basalam,
          this.$t("variantId") + " (" + panel + ")",
          obj.basalamVariantId
        );
      });
      return lines;
    },
    onCopyValue() {
      if (typeof this.copied === "function") this.copied();
      else if (
        this.productList &&
        typeof this.productList.copied === "function"
      ) {
        this.productList.copied();
      }
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.productList.loading || this.appending) return;
      if (this.currentPage >= (this.productList.pageCount || 1)) return;
      this.appending = true;
      this.productList.tableOptions = Object.assign(
        {},
        this.productList.tableOptions,
        { page: this.currentPage + 1 }
      );
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && i.id === v);
          if (found && (found.name || found.text)) {
            names.push(found.name || found.text);
          }
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    isProductStatusOn(value) {
      const cur = this.productList.productStatus;
      if (Array.isArray(cur)) return cur.indexOf(value) !== -1;
      return cur === value;
    },
    pickProductStatus(value) {
      // Same multi-select + clearable as desktop v-select productStatus.
      let cur = this.productList.productStatus;
      if (!Array.isArray(cur)) cur = cur != null && cur !== "" ? [cur] : [];
      const idx = cur.indexOf(value);
      if (idx >= 0) {
        cur = cur.filter(function (_, i) {
          return i !== idx;
        });
      } else {
        cur = cur.concat([value]);
      }
      this.productList.productStatus = cur.length ? cur : null;
    },
    pickInventoryStatus(value) {
      // Desktop single clearable: set value, or clear when null / same tap.
      if (value == null || this.productList.inventoryStatus === value) {
        this.productList.inventoryStatus = null;
      } else {
        this.productList.inventoryStatus = value;
      }
      this.inventoryPickerOpen = false;
    },
    toggleBundling() {
      // Same shell checkbox semantics as desktop (value="active").
      if (this.productList.bundling === "active") {
        this.productList.bundling = null;
      } else {
        this.productList.bundling = "active";
      }
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      if (key === "custom") {
        this.loadFilterCustomFields();
        this.loadFilterCustomKeys();
      }
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    loadFilterCustomFields() {
      const source = Array.isArray(this.productList.customFields)
        ? this.productList.customFields
        : [];
      this.filterCustomItems = source.map((item) => ({
        key: item && item.key != null ? item.key : null,
        value: item && item.value != null ? item.value : null,
      }));
    },
    syncFilterCustomFields() {
      this.productList.customFields = this.filterCustomItems.map((item) => ({
        key: item.key,
        value: item.value,
      }));
    },
    addFilterCustomField() {
      this.filterCustomItems.push({ key: null, value: null });
      this.syncFilterCustomFields();
    },
    removeFilterCustomField(index) {
      this.filterCustomItems.splice(index, 1);
      this.syncFilterCustomFields();
    },
    loadFilterCustomKeys() {
      clearTimeout(this.filterCustomKeyTimer);
      this.filterCustomKeyTimer = setTimeout(() => {
        this.$axios
          .get(this.$endpoint + "getdetail", {
            params: {
              select: "productCustomFields",
              search: this.filterCustomSearch,
            },
          })
          .then((res) => {
            this.filterCustomKeyItems = res.data || [];
          })
          .catch((err) => {
            if (typeof this.productList.serverErrorHandeler === "function") {
              this.productList.serverErrorHandeler(err);
            }
          });
      }, 400);
    },
    backToFilterMain() {
      if (this.filterGroup === "custom") {
        this.filterCustomSearch = null;
        this.filterCustomItems = [];
        this.filterCustomKeyItems = [];
      }
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.productList;
      if (key === "status") {
        return (
          this.hasValue(list.productStatus) ||
          this.hasValue(list.inventoryStatus) ||
          list.bundling === "active"
        );
      }
      if (key === "category") {
        return (
          this.hasValue(list.group) ||
          this.hasValue(list.cat1) ||
          this.hasValue(list.cat2) ||
          this.hasValue(list.tag) ||
          this.hasValue(list.branch)
        );
      }
      if (key === "registerar") return this.hasValue(list.registerar);
      if (key === "custom") return this.hasValue(list.customFields);
      return false;
    },
    groupSubtitle(key) {
      if (key === "status") {
        return (
          this.$t("status") +
          " · " +
          this.$t("inventoryStatus") +
          " · " +
          this.$t("showJustBundle")
        );
      }
      if (key === "category") {
        return (
          this.$t("main-group") +
          " · " +
          this.$t("other-group") +
          " · " +
          this.$t("category") +
          " · " +
          this.$t("tag") +
          " · " +
          this.$t("branch")
        );
      }
      if (key === "registerar") return this.$t("registerar");
      if (key === "custom") {
        return this.$t("invoice-mobile-custom-field-extras-subtitle");
      }
      return "";
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.appending = false;
      this.productList.tableOptions = Object.assign(
        {},
        this.productList.tableOptions,
        { page: 1 }
      );
      this.productList.getItems();
    },
    clearFilter() {
      // Reset to the same shell defaults the desktop filter starts from.
      const list = this.productList;
      list.productStatus = null;
      list.inventoryStatus = list.inventoryStatusProp || null;
      list.bundling = null;
      list.group = null;
      list.cat1 = null;
      list.cat2 = null;
      list.tag = null;
      list.branch = null;
      list.registerar = null;
      list.customFields = null;
      this.filterCustomItems = [];
      this.applyFilter();
    },
  },
};
</script>

<style scoped>
.plm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.plm--selecting .plm__list {
  padding-bottom: 88px;
}
.plm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.plm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.plm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.plm__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
}
.plm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.plm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.plm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.plm__bar-btn--create:active {
  background: rgba(91, 124, 153, 0.18);
}
.plm__bar-btn--back {
  flex: 0 0 40px;
}
.plm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.plm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
}
.plm__search-icon {
  flex: 0 0 auto;
}
.plm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #263238;
  text-align: right;
}
.plm__search-clear {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.plm__filter-btn {
  flex: 0 0 44px;
  height: 44px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.plm__filter-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.plm__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.plm__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.plm__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.plm__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.plm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.plm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.plm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.plm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.plm__state--more {
  padding: 14px 0;
}
.plm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.plm-card {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 12px;
  transition: transform 0.15s ease;
}
.plm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.plm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.plm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.plm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.plm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.plm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.plm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.plm-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.plm-card__identity {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}
.plm-card__avatar {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  background: #eceff1;
}
.plm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1px;
  text-align: right;
}
.plm-card__name-link {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;
  align-self: flex-start;
  min-width: 0;
  max-width: 100%;
  padding: 2px 0;
  margin: -2px 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #5b7c99;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-card__name-link:active {
  background: rgba(91, 124, 153, 0.12);
}
.plm-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #5b7c99;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-card__code {
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: #90a4ae;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.plm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.plm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.plm-card__amount-label {
  font-size: 12px;
  color: #78909c;
  flex: 0 0 auto;
}
.plm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-card__amount-value--muted {
  font-weight: 600;
  color: #607d8b;
  white-space: normal;
  text-align: left;
}
.plm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.plm-card__actions {
  display: flex;
  gap: 6px;
}
.plm-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  border: 0;
}
.plm-btn .v-icon {
  margin-inline-end: 2px;
}
.plm-btn--danger {
  background: rgba(229, 57, 53, 0.08);
  color: #e53935;
}
.plm-btn--danger:active {
  background: rgba(229, 57, 53, 0.16);
}
.plm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.plm-btn--primary:active {
  background: #4a6a84;
}
.plm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.plm-btn--outline:active {
  background: rgba(91, 124, 153, 0.08);
}
.plm-stock-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-stock-btn:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.15);
}
.plm-stock-btn:disabled {
  background: transparent;
  color: #90a4ae;
  cursor: default;
}
.plm__sentinel {
  height: 1px;
}

.plm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.plm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.plm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.plm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.plm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.plm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.plm-sheet__header-side--end {
  justify-content: flex-end;
}
.plm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 700;
}
.plm-sheet__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
}
.plm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.plm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.plm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.plm-sheet__name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.plm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.45;
}
.plm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 8px 0 0;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__linkrow:active {
  background: rgba(255, 255, 255, 0.95);
}
.plm-sheet__linkrow-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-sheet__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 16px 16px;
}
.plm-sheet__card {
  border: 1px solid #e8edf2;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}
.plm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f3f6;
}
.plm-sheet__row--chips {
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 4px;
  min-height: 0;
}
.plm-sheet__card .plm-sheet__row:last-child {
  border-bottom: 0;
}
.plm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.plm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-sheet__row-value--muted {
  color: #90a4ae;
  font-weight: 500;
}
.plm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.plm-sheet__row--id {
  align-items: center;
}
.plm-sheet__id-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.plm-sheet__id-icon {
  width: 18px;
  height: 18px;
  border-radius: 0;
  object-fit: contain;
}
.plm-sheet__id-value {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #78909c;
  text-align: left;
  font: inherit;
  direction: ltr;
}
.plm-sheet__actions {
  display: flex;
  flex-direction: column;
}
.plm-sheet__actions--sheet {
  margin-top: 0;
}
.plm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  text-align: right;
}
.plm-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.plm-sheet__action--danger {
  color: #e53935;
}
.plm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.plm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.plm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.plm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.plm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.plm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.plm-sheet__back {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.plm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.plm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.plm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.plm-sheet__btn--primary:active {
  background: #4d6a84;
}
.plm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.plm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.plm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.plm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-filter-nav__item:active {
  background: #f7f9fc;
}
.plm-filter-nav__item:last-child {
  border-bottom: 0;
}
.plm-filter-nav__icon {
  flex: 0 0 auto;
}
.plm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.plm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.plm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.plm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.plm-filter-fields > * {
  margin-bottom: 14px;
}
.plm-picker-host {
  display: none !important;
}
.plm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.plm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.plm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.plm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.plm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-toggle-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.plm-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 0;
  min-width: 0;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-toggle__label {
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
}
.plm-toggle__switch {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: #cfd8dc;
  flex: 0 0 auto;
  transition: background 0.15s ease;
}
.plm-toggle__switch::after {
  content: "";
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease;
}
.plm-toggle--on {
  border-color: rgba(91, 124, 153, 0.35);
  background: rgba(91, 124, 153, 0.08);
}
.plm-toggle--on .plm-toggle__switch {
  background: #5b7c99;
}
.plm-toggle--on .plm-toggle__switch::after {
  transform: translateX(-18px);
}
.plm-check-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plm-check-list--row {
  flex-direction: row;
  align-items: stretch;
}
.plm-check-list--row .plm-check-row {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  justify-content: center;
}
.plm-check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  text-align: right;
}
.plm-check-row:active {
  background: #eef4f8;
}
.plm-check-row--on {
  border-color: rgba(91, 124, 153, 0.35);
  background: rgba(91, 124, 153, 0.08);
}
.plm-check-row__label {
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
}
.plm-option-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 12px;
}
.plm-option-list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  min-height: 48px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-option-list__item:active {
  background: #f7f9fc;
}
.plm-option-list__item--on {
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.06);
}
.plm-option-list__item:last-child {
  border-bottom: 0;
}
.plm-f-field.v-input,
.plm-f-field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 14px;
}
.plm-f-field ::v-deep .v-input__slot,
.plm-f-field.v-input ::v-deep .v-input__slot {
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: none !important;
}
.plm-f-field ::v-deep .v-text-field__details,
.plm-f-field ::v-deep .v-messages {
  display: none !important;
}
.plm-cf__hint {
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
}
.plm-cf__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  margin-bottom: 12px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5b7c99;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-cf__add:active {
  background: #d9e1ec;
}
.plm-cf__empty {
  padding: 16px;
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #90a4ae;
}
.plm-cf-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
}
.plm-cf-card__type {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.plm-cf-card__type-field {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
}
.plm-cf-card__remove {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f8bbd0;
  border-radius: 10px;
  background: #fce4ec;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-cf-card__input {
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  outline: none;
  background: #fff;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
  box-sizing: border-box;
}
.plm-cf-card__input--text {
  text-align: right;
  font-weight: 600;
}
.plm-cf-card .plm-f-field {
  width: 100%;
  height: 40px !important;
  max-height: 40px !important;
  margin: 0 !important;
  overflow: hidden !important;
}
.plm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.plm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.plm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.plm-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.plm-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.plm-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.plm-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.plm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.plm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.plm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.plm-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.plm-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.plm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.plm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.plm-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.plm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.plm-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.plm-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
</style>
